import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquare, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { SupplyAgentChatPanel } from '@/modules/supply/components/SupplyAgentChatPanel';
import { SupplyAgentLogisticsPanel } from '@/modules/supply/components/SupplyAgentLogisticsPanel';
import { SupplyAgentSimulatePanel } from '@/modules/supply/components/SupplyAgentSimulatePanel';
import { SupplyAgentCompletionStrategyPicker } from '@/modules/supply/components/SupplyAgentCompletionStrategyPicker';
import { labelSessionEphemeralRule, isSessionEphemeralRuleEnabled } from '@/modules/supply/components/supplyAgentSessionLabels';
import {
  COMPLETION_STRATEGY_OPTIONS,
  completionStrategyKey,
  isCompletionRule,
  readCompletionStrategy,
  type SupplyAgentCompletionStrategy,
  type SupplyAgentRuleRecord,
} from '@/modules/supply/components/supplyAgentRuleUtils';
import { useSupplyAgentPersistedRules } from '@/modules/supply/hooks/useSupplyAgentPersistedRules';
import type { SupplyAgentSimulateResponse } from '@/services/supplyAgentService';
import {
  consumeSupplyAgentForceOpen,
  SUPPLY_AGENT_OPEN_EVENT,
  SUPPLY_AGENT_RULES_CHANGED_EVENT,
} from '@/modules/supply/components/supplyAgentDrawer';

const DRAWER_OPEN_KEY = 'supplyAgent.drawerOpen';
const DRAWER_OPEN_CONFIG_KEY = 'supplyAgent.rulesConfig.drawerOpen';
const DRAWER_WIDTH_KEY = 'supplyAgent.drawerWidthPct';
const CHAT_WIDTH_KEY = 'supplyAgent.chatWidthPct';
const DEFAULT_WIDTH_PCT = 70;
const MIN_WIDTH_PCT = 40;
const MAX_WIDTH_PCT = 92;
/** Ancho del chat como % del cuerpo del drawer (desktop). ~1fr de 1.4fr+1fr. */
const DEFAULT_CHAT_PCT = 42;
const MIN_CHAT_PCT = 28;
const MAX_CHAT_PCT = 65;

interface SupplyAgentWorkbenchProps {
  supplierId: number | null;
  warehouseId: number;
  supplierName?: string | null;
  /** Si True, el agente alinea sus ajustes al MP (toggle tabla); no redondea el pedido entero. */
  applyMoqMp?: boolean;
  /**
   * Módulo Reglas: solo configuración de reglas + logística (sin impacto de pedido ni líneas).
   */
  configOnly?: boolean;
  onSimulationChange?: (simulation: SupplyAgentSimulateResponse | null) => void;
  onSelectSupplier?: (supplierId: number, supplierName?: string) => void;
}

function readInitialOpen(configOnly: boolean): boolean {
  if (consumeSupplyAgentForceOpen()) return true;
  try {
    const key = configOnly ? DRAWER_OPEN_CONFIG_KEY : DRAWER_OPEN_KEY;
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function clampWidthPct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WIDTH_PCT;
  return Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, Math.round(value)));
}

function clampChatPct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_CHAT_PCT;
  return Math.min(MAX_CHAT_PCT, Math.max(MIN_CHAT_PCT, Math.round(value)));
}

function readInitialWidthPct(): number {
  try {
    const raw = localStorage.getItem(DRAWER_WIDTH_KEY);
    if (raw == null || raw === '') return DEFAULT_WIDTH_PCT;
    return clampWidthPct(Number(raw));
  } catch {
    return DEFAULT_WIDTH_PCT;
  }
}

function readInitialChatPct(): number {
  try {
    const raw = localStorage.getItem(CHAT_WIDTH_KEY);
    if (raw == null || raw === '') return DEFAULT_CHAT_PCT;
    return clampChatPct(Number(raw));
  } catch {
    return DEFAULT_CHAT_PCT;
  }
}

export function SupplyAgentWorkbench({
  supplierId,
  warehouseId,
  supplierName,
  applyMoqMp = true,
  configOnly = false,
  onSimulationChange,
  onSelectSupplier,
}: SupplyAgentWorkbenchProps) {
  const { t } = useTranslation();
  const { formatRounded } = useCurrency();
  const [open, setOpen] = useState(() => readInitialOpen(configOnly));
  const [widthPct, setWidthPct] = useState(readInitialWidthPct);
  const [chatWidthPct, setChatWidthPct] = useState(readInitialChatPct);
  const [rulesRefreshToken, setRulesRefreshToken] = useState(0);
  const [leftTab, setLeftTab] = useState<'rules' | 'logistics'>('rules');
  const [expandedSessionIdx, setExpandedSessionIdx] = useState<number | null>(null);
  const resizingRef = useRef(false);
  const chatResizingRef = useRef(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const persistedRules = useSupplyAgentPersistedRules({
    supplierId,
    warehouseId,
    refreshToken: rulesRefreshToken,
    autoSimulate: !configOnly,
    applyLineBoxMultiples: applyMoqMp,
  });
  const warehouseMode = supplierId == null;

  useEffect(() => {
    if (configOnly) return;
    onSimulationChange?.(persistedRules.simulation);
  }, [configOnly, persistedRules.simulation, onSimulationChange]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onRulesChanged = () => setRulesRefreshToken((n) => n + 1);
    window.addEventListener(SUPPLY_AGENT_OPEN_EVENT, onOpen);
    window.addEventListener(SUPPLY_AGENT_RULES_CHANGED_EVENT, onRulesChanged);
    return () => {
      window.removeEventListener(SUPPLY_AGENT_OPEN_EVENT, onOpen);
      window.removeEventListener(SUPPLY_AGENT_RULES_CHANGED_EVENT, onRulesChanged);
    };
  }, []);

  useEffect(() => {
    try {
      const key = configOnly ? DRAWER_OPEN_CONFIG_KEY : DRAWER_OPEN_KEY;
      localStorage.setItem(key, open ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [open, configOnly]);

  useEffect(() => {
    try {
      localStorage.setItem(DRAWER_WIDTH_KEY, String(widthPct));
    } catch {
      /* ignore */
    }
  }, [widthPct]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAT_WIDTH_KEY, String(chatWidthPct));
    } catch {
      /* ignore */
    }
  }, [chatWidthPct]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const onResizePointerMove = useCallback((e: PointerEvent) => {
    if (!resizingRef.current) return;
    const vw = window.innerWidth || 1;
    const next = ((vw - e.clientX) / vw) * 100;
    setWidthPct(clampWidthPct(next));
  }, []);

  const stopResize = useCallback(() => {
    if (!resizingRef.current) return;
    resizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onResizePointerMove);
    window.removeEventListener('pointerup', stopResize);
  }, [onResizePointerMove]);

  const startResize = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onResizePointerMove);
      window.addEventListener('pointerup', stopResize);
    },
    [onResizePointerMove, stopResize],
  );

  const onChatResizePointerMove = useCallback((e: PointerEvent) => {
    if (!chatResizingRef.current || !bodyRef.current) return;
    const rect = bodyRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const chatPx = rect.right - e.clientX;
    setChatWidthPct(clampChatPct((chatPx / rect.width) * 100));
  }, []);

  const stopChatResize = useCallback(() => {
    if (!chatResizingRef.current) return;
    chatResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onChatResizePointerMove);
    window.removeEventListener('pointerup', stopChatResize);
  }, [onChatResizePointerMove]);

  const startChatResize = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      chatResizingRef.current = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onChatResizePointerMove);
      window.addEventListener('pointerup', stopChatResize);
    },
    [onChatResizePointerMove, stopChatResize],
  );

  useEffect(
    () => () => {
      stopResize();
      stopChatResize();
    },
    [stopResize, stopChatResize],
  );

  const sessionCount = persistedRules.sessionEphemeralRules.length;
  const savedActiveCount = persistedRules.rules.filter((r) => r.is_active !== false).length;
  const sim = persistedRules.simulation;
  const deltaAmount =
    sim != null ? sim.after.total_amount_euros - sim.before.total_amount_euros : null;
  const deltaLabel =
    deltaAmount == null
      ? null
      : `${deltaAmount >= 0 ? '+' : ''}${formatRounded(deltaAmount)}`;

  const hasStatus =
    sessionCount > 0 ||
    savedActiveCount > 0 ||
    deltaLabel != null ||
    Boolean(supplierName) ||
    warehouseMode;

  const statusStrip = hasStatus ? (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {warehouseMode ? (
        <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-800">
          {t('supply.supplyAgent.warehouseModeBadge')}
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs text-violet-800">
          {t('supply.supplyAgent.supplierFilterBadge')}
        </span>
      )}
      {(sessionCount > 0 || savedActiveCount > 0) && (
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700">
          {t('supply.supplyAgent.statusPill', {
            session: sessionCount,
            saved: savedActiveCount,
          })}
        </span>
      )}
      {deltaLabel != null && (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${
            deltaAmount != null && deltaAmount < 0
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : deltaAmount != null && deltaAmount > 0
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-gray-200 bg-gray-50 text-gray-700'
          }`}
        >
          {t('supply.supplyAgent.statusDelta', { delta: deltaLabel })}
        </span>
      )}
      {supplierName ? (
        <span className="max-w-[14rem] truncate text-xs text-gray-500">
          {t('supply.supplyAgent.supplierContext', { name: supplierName })}
        </span>
      ) : null}
    </div>
  ) : null;

  const drawer = createPortal(
    <>
      <button
        type="button"
        aria-label={t('supply.supplyAgent.closeDrawer')}
        tabIndex={open ? 0 : -1}
        className={`fixed inset-0 z-[80] bg-slate-900/25 transition-opacity ${
          open ? 'opacity-100' : 'pointer-events-none hidden opacity-0'
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-[85] flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out max-md:!w-full md:min-w-[36rem] md:max-w-[96vw] ${
          open ? 'translate-x-0' : 'pointer-events-none hidden translate-x-full'
        }`}
        style={{ width: `${widthPct}vw` }}
        role="dialog"
        aria-modal={open}
        aria-hidden={!open}
        aria-label={t('supply.supplyAgent.title')}
      >
        <button
          type="button"
          aria-label={t('supply.supplyAgent.resizeDrawer')}
          title={t('supply.supplyAgent.resizeDrawer')}
          onPointerDown={startResize}
          className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize touch-none bg-transparent hover:bg-sky-400/40 md:block"
        />
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <Sparkles className="h-4 w-4 shrink-0 text-sky-600" />
              {t('supply.supplyAgent.title')}
            </h3>
            {supplierName ? (
              <p className="mt-0.5 truncate text-xs text-slate-500">
                {t('supply.supplyAgent.supplierContext', { name: supplierName })}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-200/70 hover:text-slate-800"
            aria-label={t('supply.supplyAgent.closeDrawer')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div
          ref={bodyRef}
          className="flex min-h-0 flex-1 flex-col md:flex-row"
        >
          <section
            className="flex min-h-0 min-w-0 flex-1 flex-col border-b border-slate-200 md:border-b-0"
            aria-label={
              configOnly
                ? t('supply.supplyAgent.rulesTab')
                : t('supply.supplyAgent.rulesSimSection')
            }
          >
            <div
              className="flex shrink-0 items-center gap-1 border-b border-slate-100 px-3 py-1.5"
              role="tablist"
              aria-label={
                configOnly
                  ? t('supply.supplyAgent.rulesTab')
                  : t('supply.supplyAgent.rulesSimSection')
              }
            >
              <button
                type="button"
                role="tab"
                aria-selected={leftTab === 'rules'}
                onClick={() => setLeftTab('rules')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                  leftTab === 'rules'
                    ? 'bg-sky-100 text-sky-800'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                }`}
              >
                {t('supply.supplyAgent.rulesTab')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={leftTab === 'logistics'}
                onClick={() => setLeftTab('logistics')}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors ${
                  leftTab === 'logistics'
                    ? 'bg-sky-100 text-sky-800'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
                }`}
              >
                {t('supply.supplyAgent.logisticsTab')}
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
              <div className={leftTab === 'rules' ? 'space-y-3' : 'hidden'}>
              {sessionCount > 0 && (
                <div className="rounded-md border border-sky-100 bg-sky-50 px-2.5 py-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-sky-900">
                      {t('supply.supplyAgent.sessionEphemeralTitle', {
                        count: sessionCount,
                      })}
                    </p>
                    {sessionCount > 1 && (
                      <button
                        type="button"
                        onClick={() => persistedRules.clearSessionEphemeralRules()}
                        className="shrink-0 text-xs font-medium text-sky-700 hover:text-sky-900"
                      >
                        {t('supply.supplyAgent.clearAllSessionEphemeral')}
                      </button>
                    )}
                  </div>
                  <p className="mb-1.5 text-[10px] leading-snug text-sky-700/90">
                    {t('supply.supplyAgent.sessionEphemeralToggleHint')}
                  </p>
                  <ul className="space-y-1">
                    {persistedRules.sessionEphemeralRules.map((rule, idx) => {
                      const enabled = isSessionEphemeralRuleEnabled(rule);
                      const asRecord = rule as SupplyAgentRuleRecord;
                      const canPick = isCompletionRule(asRecord);
                      const key = completionStrategyKey(asRecord);
                      const perRule = key ? persistedRules.ruleCompletionStrategies[key] : undefined;
                      const resolved: SupplyAgentCompletionStrategy =
                        perRule ||
                        persistedRules.completionStrategy ||
                        readCompletionStrategy(asRecord) ||
                        'coverage_layers';
                      const strategyLabel = t(
                        COMPLETION_STRATEGY_OPTIONS.find((option) => option.id === resolved)
                          ?.labelKey || 'supply.supplyAgent.completionStrategyCoverage',
                      );
                      const expanded = expandedSessionIdx === idx;
                      return (
                        <li
                          key={`${String(rule.rule_type || 'rule')}-${idx}`}
                          className={`text-xs ${enabled ? 'text-sky-900' : 'text-sky-500'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center gap-1.5">
                              <input
                                type="checkbox"
                                className="h-3 w-3 shrink-0 accent-sky-600"
                                checked={enabled}
                                onChange={(e) =>
                                  persistedRules.setSessionEphemeralRuleEnabled(idx, e.target.checked)
                                }
                                aria-label={t('supply.supplyAgent.sessionToggle')}
                              />
                              {canPick ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setExpandedSessionIdx((prev) => (prev === idx ? null : idx))
                                  }
                                  title={t('supply.supplyAgent.completionStrategyOnRule')}
                                  className={`min-w-0 truncate text-left ${enabled ? '' : 'opacity-70 line-through'}`}
                                >
                                  {labelSessionEphemeralRule(rule)}
                                  {perRule ? (
                                    <span className="ml-1 text-[10px] text-indigo-700">
                                      · {strategyLabel}
                                    </span>
                                  ) : null}
                                </button>
                              ) : (
                                <span className={`truncate ${enabled ? '' : 'opacity-70 line-through'}`}>
                                  {labelSessionEphemeralRule(rule)}
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => persistedRules.removeSessionEphemeralRule(idx)}
                              className="inline-flex shrink-0 items-center gap-0.5 text-sky-700 hover:text-sky-900"
                              title={t('supply.supplyAgent.removeSessionEphemeral')}
                            >
                              <X className="h-3.5 w-3.5" />
                              {t('supply.supplyAgent.removeSessionEphemeral')}
                            </button>
                          </div>
                          {canPick && expanded ? (
                            <div className="mt-1 pl-5">
                              <SupplyAgentCompletionStrategyPicker
                                value={resolved}
                                disabled={persistedRules.simulating}
                                compact
                                onChange={(strategy) =>
                                  persistedRules.setRuleCompletionStrategy(asRecord, strategy)
                                }
                              />
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <SupplyAgentSimulatePanel
                supplierId={supplierId}
                warehouseId={warehouseId}
                supplierName={null}
                persistedRules={persistedRules}
                embedded
                configOnly={configOnly}
                onSelectSupplier={onSelectSupplier}
              />
              </div>
              <div className={leftTab === 'logistics' ? '' : 'hidden'}>
              <SupplyAgentLogisticsPanel
                supplierId={supplierId}
                warehouseId={warehouseId}
                hideTitle
                hideApplyToOrder={configOnly}
                onApplyToSuggestedOrder={
                  configOnly ? undefined : (simulation) => onSimulationChange?.(simulation)
                }
              />
              </div>
            </div>
          </section>

          <button
            type="button"
            aria-label={t('supply.supplyAgent.resizeChat')}
            title={t('supply.supplyAgent.resizeChat')}
            onPointerDown={startChatResize}
            className="relative z-10 hidden w-1.5 shrink-0 cursor-col-resize touch-none bg-transparent hover:bg-sky-400/40 md:block"
          />

          <section
            className="flex min-h-0 w-full flex-col bg-white md:w-[var(--agent-chat-w)] md:shrink-0"
            style={{ ['--agent-chat-w' as string]: `${chatWidthPct}%` }}
            aria-label={t('supply.supplyAgent.chatTitle')}
          >
            <div className="flex shrink-0 items-center gap-1.5 border-b border-slate-100 px-4 py-2">
              <MessageSquare className="h-3.5 w-3.5 text-sky-600" />
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {t('supply.supplyAgent.chatTitle')}
              </p>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <SupplyAgentChatPanel
                key={warehouseId}
                supplierId={supplierId}
                warehouseId={warehouseId}
                savedRules={persistedRules.rules}
                loadingSavedRules={persistedRules.loadingRules}
                sessionEphemeralRules={persistedRules.sessionEphemeralRules}
                onRulesChanged={() => setRulesRefreshToken((value) => value + 1)}
                onApplySessionRules={persistedRules.applySessionEphemeralRules}
                onRemoveSessionRule={persistedRules.removeSessionEphemeralRule}
                onToggleSessionRule={persistedRules.setSessionEphemeralRuleEnabled}
                onClearSessionRules={persistedRules.clearSessionEphemeralRules}
                layout="drawer"
                hideSavedRules
                hideSessionRules
                runSimulation={!configOnly}
              />
            </div>
          </section>
        </div>
      </aside>
    </>,
    document.body,
  );

  return (
    <>
      {statusStrip}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[70] inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-sky-700"
          aria-label={t('supply.supplyAgent.openDrawer')}
        >
          <Sparkles className="h-4 w-4" />
          {t('supply.supplyAgent.fabLabel')}
        </button>
      )}
      {drawer}
    </>
  );
}
